import { accraDay, conditionsFor, dailyChallenge, describeConditions, type ChallengeInfo, type LeaderboardEntry } from '@accra/shared';
import { PLAYER_VEHICLES, type PlayerVehicleId } from '../../content/playerVehicles';
import { routeById } from '../../content/routes';
import { el } from '../components/el';
import { badgeIcons } from '../components/badgeIcons';

/** Today's daily challenge: the road and trotro everyone gets, who's on top, and a Play button. */
export class DailyCard {
  readonly root = el('div', 'daily-card');
  private readonly board = el('div', 'daily-board');

  constructor(onPlay: () => void) {
    const play = el('button', 'btn small primary', 'Play');
    play.addEventListener('click', onPlay);
    const head = el('div', 'daily-head');
    head.append(el('strong', '', '📅 Daily challenge'), play);
    this.root.append(head, el('small', 'daily-what'), this.board);
    this.refresh(Date.now());
  }

  /** Re-reads today's challenge (it changes at midnight, Accra time). */
  refresh(now: number): void {
    const today = dailyChallenge(accraDay(now));
    const vehicle = PLAYER_VEHICLES[today.vehicle as PlayerVehicleId]?.name ?? today.vehicle;
    const what = this.root.querySelector('.daily-what');
    const weather = describeConditions(conditionsFor(today.seed));
    if (what) what.textContent = `${routeById(today.route).name} · ${vehicle}${weather ? ` · ${weather.chip}` : ''} · same for everyone`;
  }

  setBoard(rows: readonly LeaderboardEntry[] | null): void {
    if (!rows || rows.length === 0) {
      this.board.replaceChildren(el('small', 'muted', rows ? 'Nobody has played yet. Be the first!' : ''));
      return;
    }
    this.board.replaceChildren(
      ...rows.slice(0, 3).map((row) => {
        const line = el('div', 'daily-row');
        line.append(el('span', 'rank', `#${row.rank}`), el('span', 'name', row.name), badgeIcons(row.badges), el('span', 'score', row.score.toLocaleString()));
        return line;
      }),
    );
  }
}

/** Shown when you open a friend's challenge link. */
export class ChallengeBanner {
  readonly root = el('div', 'challenge-banner');

  constructor(private readonly onAccept: () => void, private readonly onDismiss: () => void) {
    this.root.hidden = true;
  }

  show(info: ChallengeInfo | null): void {
    this.root.hidden = info === null;
    if (!info) return;
    const vehicle = PLAYER_VEHICLES[info.vehicle as PlayerVehicleId]?.name ?? info.vehicle;
    const accept = el('button', 'btn small primary', "Let's go!");
    accept.addEventListener('click', this.onAccept);
    const dismiss = el('button', 'btn small', 'Not now');
    dismiss.addEventListener('click', this.onDismiss);
    const text = el('div', 'challenge-text');
    text.append(
      el('strong', '', `${info.name} challenged you!`),
      el('span', '', `Beat ${info.score.toLocaleString()} on ${info.route ? routeById(info.route).name : 'their road'} in the ${vehicle}.`),
      el('small', 'muted', info.attempts > 0 ? `${info.attempts} friend${info.attempts === 1 ? ' has' : 's have'} tried${info.best ? ` · best ${info.best.name} ${info.best.score.toLocaleString()}` : ''}` : 'Nobody has tried it yet.'),
    );
    const actions = el('div', 'challenge-actions');
    actions.append(accept, dismiss);
    this.root.replaceChildren(text, actions);
  }
}
